import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronRight, Calendar, Clock } from 'lucide-react';
import { StaffMetrics } from '../../types/ltss';
import { PenaltyIndicator } from './PenaltyIndicator';

interface StaffTableProps {
  staff: StaffMetrics[];
}

type SortField = 'staffName' | 'totalServices' | 'totalHours' | 'manualCorrections' | 'cleanRate';
type SortDirection = 'asc' | 'desc';

export function StaffTable({ staff }: StaffTableProps) {
  const [sortField, setSortField] = useState<SortField>('manualCorrections');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedStaff, setExpandedStaff] = useState<Set<string>>(new Set());

  const toggleExpanded = (staffName: string) => {
    const newExpanded = new Set(expandedStaff);
    if (newExpanded.has(staffName)) {
      newExpanded.delete(staffName);
    } else {
      newExpanded.add(staffName);
    }
    setExpandedStaff(newExpanded);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedStaff = [...staff].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * modifier;
    }
    return ((aVal as number) - (bVal as number)) * modifier;
  });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4" />
      : <ChevronDown className="w-4 h-4" />;
  };

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-800/50">
            <tr>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-200"
                onClick={() => handleSort('staffName')}
              >
                <div className="flex items-center gap-1">
                  Staff Name
                  <SortIcon field="staffName" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-200"
                onClick={() => handleSort('totalServices')}
              >
                <div className="flex items-center gap-1">
                  Services
                  <SortIcon field="totalServices" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-200"
                onClick={() => handleSort('totalHours')}
              >
                <div className="flex items-center gap-1">
                  Hours
                  <SortIcon field="totalHours" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-200"
                onClick={() => handleSort('manualCorrections')}
              >
                <div className="flex items-center gap-1">
                  Penalty Points
                  <SortIcon field="manualCorrections" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-200"
                onClick={() => handleSort('cleanRate')}
              >
                <div className="flex items-center gap-1">
                  Clean Rate
                  <SortIcon field="cleanRate" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {sortedStaff.map((s) => (
              <React.Fragment key={s.staffId || s.staffName}>
                <tr 
                  className="hover:bg-slate-800/30 cursor-pointer"
                  onClick={() => toggleExpanded(s.staffName)}
                >
                  <td className="px-4 py-3 text-sm text-slate-200 font-medium">
                    <div className="flex items-center gap-2">
                      <ChevronRight 
                        className={`w-4 h-4 text-slate-500 transition-transform ${
                          expandedStaff.has(s.staffName) ? 'rotate-90' : ''
                        }`} 
                      />
                      {s.staffName}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    {s.totalServices}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">
                    <span className="text-white">{s.billableHours.toFixed(1)}</span>
                    {s.totalHours !== s.billableHours && (
                      <span className="text-xs text-orange-400 ml-1">
                        ({s.totalHours.toFixed(1)})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 w-40">
                    <PenaltyIndicator count={s.manualCorrections} correctionDates={s.manualCorrectionDates} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`${s.cleanRate >= 80 ? 'text-green-400' : s.cleanRate >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {s.cleanRate}%
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {s.penaltyStatus === 'exceeded' && (
                      <span className="px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded">
                        EXCEEDED
                      </span>
                    )}
                    {s.penaltyStatus === 'risk' && (
                      <span className="px-2 py-1 text-xs font-medium bg-orange-500/20 text-orange-400 rounded">
                        AT RISK
                      </span>
                    )}
                    {s.penaltyStatus === 'ok' && (
                      <span className="px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400 rounded">
                        OK
                      </span>
                    )}
                  </td>
                </tr>
                {/* Weekly Hours Breakdown - Expandable Row */}
                {expandedStaff.has(s.staffName) && (
                  <tr className="bg-slate-800/50">
                    <td colSpan={6} className="px-4 py-4">
                      <div className="ml-6">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-blue-400" />
                            Weekly Hours Breakdown (Payroll)
                          </h4>
                        </div>
                        {s.weeklyHours && s.weeklyHours.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {s.weeklyHours.map((week, idx) => (
                              <div 
                                key={idx}
                                className="bg-slate-700/50 rounded-lg p-3 border border-slate-600"
                              >
                                <p className="text-xs text-slate-400 mb-2">{week.weekLabel}</p>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-400">Billable:</span>
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-3 h-3 text-green-400" />
                                      <span className="text-sm font-semibold text-green-400">
                                        {week.billableHours.toFixed(1)} hrs
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-400">Actual:</span>
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-3 h-3 text-blue-400" />
                                      <span className={`text-sm font-medium ${week.hours !== week.billableHours ? 'text-orange-400' : 'text-blue-400'}`}>
                                        {week.hours.toFixed(1)} hrs
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-600">
                                  {week.services} service{week.services !== 1 ? 's' : ''}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">No weekly data available</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {staff.length === 0 && (
        <div className="p-8 text-center text-slate-400">
          No staff data available
        </div>
      )}
    </div>
  );
}
